import React, { useContext, useEffect, useState, useRef } from "react";
import Sidebar from "./Sidebar";
import { Container, useMediaQuery } from "@mui/material";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import Loader from "../utils/Loading";
import { SidebarContext } from "../hooks/UseSidebar";
import Header from "./Header";
import "../App.css";
import HeaderName from "../utils/headerName.json";
import { HeaderNameType } from "../utils/HeaderRoutesType";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { useSubscription } from "../context/SubscriptionContext";
import { useAuth } from "../hooks/useAuth";
import { Role } from "../constant/RoleConstants";

const MainLayout: React.FC = () => {
  const { side, toggleSide, setSide } = useContext(SidebarContext);
  const boxRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const location = useLocation();
  const navidate = useNavigate();
  const TypedHeaderName: HeaderNameType = HeaderName;
  const isBelow1300 = useMediaQuery("(max-width:1300px)");
  const isAbove1300 = useMediaQuery("(min-width:1301px)");
  const isMobile = useMediaQuery("(max-width:1200px)");
  const { checkSubscription } = useSubscription();
  const { user } = useAuth();

  const theme = createTheme({
    components: {
      MuiTooltip: {
        defaultProps: {
          enterDelay: 350,
          leaveDelay: 80,
          enterNextDelay: 200,
        },
        styleOverrides: {
          tooltip: {
            fontSize: "0.8125rem",
            fontWeight: 600,
            padding: "9px 16px",
            backgroundColor: "#244565",
            color: "#fff",
            zIndex: 10000,
            borderRadius: "10px",
            border: "1px solid rgba(132, 225, 204, 0.28)",
            boxShadow: "0 10px 28px rgba(15, 35, 55, 0.38)",
          },
          arrow: {
            color: "#244565",
          },
        },
      },
    },
  });

  const handleSidebarToggle = (expanded: boolean) => {
    setSidebarExpanded(expanded);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  const handleOutsideClick = () => {
    if (side) {
      setSide(false);
    }
  };

  const getPageName = () => {
    const currentPath = location.pathname;
    for (const [key, value] of Object.entries(TypedHeaderName)) {
      const routePattern = new RegExp(`^${key.replace(/:\w+/g, "[^/]+")}$`);
      if (routePattern.test(currentPath)) {
        return value;
      }
    }
    return "Dashboard";
  };

  // Set document title dynamically based on page
  useEffect(() => {
    const pageName = getPageName();
    document.title = pageName ? `${pageName} - Sprooze` : "Sprooze";
  }, [location.pathname]);

  useEffect(() => {
    if (user?.status !== Role.HIRING_MANAGER) {
      checkSubscription();
    }
  }, [user?.status, checkSubscription]);

  useEffect(() => {
    if (isMobile || isBelow1300) {
      setSide(false);
    }
  }, [isMobile, isBelow1300]);

  return (
    <div
      className={`main-layout ${
        isAbove1300
          ? sidebarExpanded
            ? "main-layout--sidebar-expanded"
            : "main-layout--sidebar-collapsed"
          : ""
      }`}
    >
      <ThemeProvider theme={theme}>
        <Sidebar visible={side} onToggle={handleSidebarToggle} />
      </ThemeProvider>
      {side && (isMobile || isBelow1300) && (
        <div className="overlay" onClick={handleOutsideClick}></div>
      )}
      <div className="content-wrapper content">
        <Header headerRef={boxRef} pageName={getPageName()} isShow={true} />
        <div className="content-wrappers">
          <Container
            style={{
              flexGrow: 1,
              boxSizing: "border-box",
              height: "100%",
              maxWidth: "100%",
            }}
          >
            <Outlet />
          </Container>
        </div>
      </div>
    </div>
  );
};

export default MainLayout;
